import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithAuth } from '../base-query';

/**
 * Analytics page feature slice.
 *
 * Endpoints read from the Postgres mirror tables (`credit_ledger`,
 * `enrichment_metrics`, `lead_lists`) — never from PostHog directly.
 * The backend enforces a staff-vs-self auth gate on every endpoint:
 * non-admins are silently scoped to their own user_id; admins can pass
 * any user_id or `group_by=user` for a per-member breakdown.
 */

export interface AnalyticsRangeParams {
  /** Inclusive start date — ``YYYY-MM-DD`` (UTC). */
  start: string;
  /** Inclusive end date — ``YYYY-MM-DD`` (UTC). */
  end: string;
  /** Clerk user_id to filter on. Non-admins must pass own id or omit. */
  userId?: string;
  /** Set to ``user`` to request a per-member breakdown (admin only). */
  groupBy?: 'user';
}

export interface DailyBucket {
  date: string;
  value: number;
}

export interface MemberBreakdownRow {
  user_id: string;
  enrichments_run: number;
  enrichments_succeeded: number;
  credits_spent: number;
}

export interface OverviewStats {
  total_lists: number;
  total_leads: number;
  leads_added: number;
  // Distinct leads enriched in the selected range (not provider attempts).
  leads_enriched: number;
  // Subset of `leads_enriched` with reliable email or any personal phone.
  leads_with_contact: number;
  credits_spent: number;
}

export interface DailyEnrichmentBreakdown {
  date: string;
  enrichment: number;
  smart_column: number;
}

export interface FunnelStats {
  total_leads: number;
  enriched: number;
  email_found: number;
  phone_found: number;
}

export interface PlatformHealth {
  score: number;
  quality_label: string;
  coverage_pct: number;
  freshness_pct: number;
}

export interface WeeklyKpi {
  label: string;
  value: number;
  sparkline: number[];
}

export interface RecentActivityItem {
  id: string;
  kind: 'enrichment' | 'smart_column' | 'list_created';
  title: string;
  list_name: string | null;
  occurred_at: string;
}

export interface OverviewTopListRow {
  id: string;
  name: string;
  size: number;
  enriched_count: number;
  enriched_pct: number;
  modified_at: string;
}

export interface OverviewResponse {
  range_start: string;
  range_end: string;
  user_id: string | null;
  is_admin_view: boolean;
  stats: OverviewStats;
  daily_enrichments: DailyBucket[];
  daily_credits: DailyBucket[];
  daily_new_lists: DailyBucket[];
  daily_enrichments_by_kind: DailyEnrichmentBreakdown[];
  lead_sources: SourceCount[];
  funnel: FunnelStats;
  platform_health: PlatformHealth;
  weekly_kpis: WeeklyKpi[];
  recent_activity: RecentActivityItem[];
  by_member: MemberBreakdownRow[] | null;
  top_lists?: OverviewTopListRow[];
  freshness?: FreshnessSegment[];
}

export interface FreshnessSegment {
  label: string;
  value: number;
}

export interface ListsStats {
  total_lists: number;
  total_leads: number;
  avg_list_size: number;
  enriched_leads: number;
  enriched_rate: number;
}

export interface TopListRow {
  id: string;
  name: string;
  size: number;
  enriched_count: number;
  enriched_pct: number;
  origin: string;
  crm_sync: boolean;
  modified_at: string;
}

export interface SourceCount {
  origin: string;
  count: number;
}

export interface QualityBucket {
  label: string;
  count: number;
  pct: number;
}

export interface CrmSyncRow {
  provider: string;
  list_count: number;
  last_sync: string | null;
  errors: number;
}

export interface IndustryBucket {
  industry: string;
  count: number;
}

export interface ListsResponse {
  range_start: string;
  range_end: string;
  user_id: string | null;
  is_admin_view: boolean;
  stats: ListsStats;
  top_lists: TopListRow[];
  by_source: SourceCount[];
  daily_new_lists: DailyBucket[];
  quality_distribution?: QualityBucket[];
  crm_sync_status?: CrmSyncRow[];
  contacts_by_industry?: IndustryBucket[];
}

export interface AnalyticsMember {
  user_id: string;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  is_self: boolean;
}

export interface MembersResponse {
  is_admin_view: boolean;
  members: AnalyticsMember[];
}

export interface CreditsStats {
  total_spent: number;
  available: number;
  daily_avg: number;
  cost_per_enriched_lead: number;
}

export interface CreditOperationRow {
  operation: string;
  operation_kind: string;
  credits: number;
  count: number;
}

export interface CreditFeatureRow {
  operation_kind: string;
  /** Spend category: a reservation's purpose, else operation_kind. */
  category?: string;
  credits: number;
}

export interface CreditMemberRow {
  user_id: string;
  credits: number;
  count: number;
}

export interface TopCreditListRow {
  id: string;
  name: string;
  credits: number;
  pct_of_total: number;
  primary_operation_kind: string;
  primary_category?: string;
}

export interface MonthlyComparisonRow {
  operation_kind: string;
  category?: string;
  this_month: number;
  last_month: number;
  change_pct: number;
}

export interface CreditsResponse {
  range_start: string;
  range_end: string;
  user_id: string | null;
  is_admin_view: boolean;
  stats: CreditsStats;
  daily_credits: DailyBucket[];
  by_operation: CreditOperationRow[];
  by_feature: CreditFeatureRow[];
  by_member: CreditMemberRow[];
  top_lists: TopCreditListRow[];
  monthly_comparison?: MonthlyComparisonRow[];
}

export interface TeamMemberRow {
  user_id: string;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  role: 'admin' | 'member';
  lists_owned: number;
  leads_enriched: number;
  emails_found: number;
  phones_found: number;
  credits_used: number;
  smart_column_runs: number;
  crm_synced_lists: number;
  weekly_activity: number[];
  last_active: string | null;
}

export interface TeamStats {
  total_members: number;
  active_members: number;
  total_credits_used: number;
  total_enrichments: number;
}

export interface TeamLeaderboardRow {
  user_id: string;
  name: string;
  enrichments: number;
  success_rate: number;
}

export interface TeamResponse {
  range_start: string;
  range_end: string;
  is_admin_view: boolean;
  stats: TeamStats;
  members: TeamMemberRow[];
  leaderboard: TeamLeaderboardRow[];
  daily_active_users: DailyBucket[];
}

export interface EnrichmentTabStats {
  // Distinct leads enriched in the selected range.
  leads_enriched: number;
  // Subset with reliable email or any personal phone.
  leads_with_contact: number;
  // leads_with_contact / leads_enriched, 0..1.
  contact_rate: number;
  avg_duration_ms: number | null;
}

export interface ProviderStat {
  provider: string;
  runs: number;
  successful: number;
  success_rate: number;
}

export interface FieldCoverageStat {
  field: string;
  populated: number;
  total: number;
  pct: number;
}

export interface EmailVsPhoneSuccess {
  email_pct: number;
  phone_pct: number;
}

export interface CompletenessSegment {
  label: string;
  value: number;
}

export interface RecentEnrichmentJob {
  list_id: string;
  list_name: string;
  leads: number;
  emails_found: number;
  phones_found: number;
  success_rate: number;
  avg_duration_ms: number | null;
  occurred_at: string;
}

export interface AgentTabStats {
  total_runs: number;
  successful: number;
  success_rate: number;
  avg_duration_ms: number | null;
}

export interface AgentTypeStat {
  column_type: string;
  runs: number;
  successful: number;
}

export interface RecentAgentRun {
  list_id: string;
  list_name: string;
  column_type: string;
  runs: number;
  success_rate: number;
  occurred_at: string;
}

export interface EnrichmentTabResponse {
  range_start: string;
  range_end: string;
  user_id: string | null;
  is_admin_view: boolean;
  stats: EnrichmentTabStats;
  daily_volume: DailyBucket[];
  by_provider: ProviderStat[];
  funnel: FunnelStats;
  field_coverage: FieldCoverageStat[];
  email_vs_phone: EmailVsPhoneSuccess;
  freshness: FreshnessSegment[];
  completeness: CompletenessSegment[];
  recent_jobs: RecentEnrichmentJob[];
  agent_stats: AgentTabStats;
  daily_agent_volume: DailyBucket[];
  by_agent_type: AgentTypeStat[];
  recent_agent_runs: RecentAgentRun[];
}

function buildAnalyticsQuery(
  path: string,
  { start, end, userId, groupBy }: AnalyticsRangeParams
): string {
  const params = new URLSearchParams({ start, end });
  if (userId) params.set('user_id', userId);
  if (groupBy) params.set('group_by', groupBy);
  return `${path}?${params.toString()}`;
}

export const analyticsApi = createApi({
  reducerPath: 'analyticsApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['Overview', 'Lists', 'Members', 'Credits', 'Team', 'Enrichment'],
  endpoints: (builder) => ({
    getAnalyticsOverview: builder.query<OverviewResponse, AnalyticsRangeParams>(
      {
        query: (args) => buildAnalyticsQuery('/analytics/overview', args),
        providesTags: ['Overview'],
      }
    ),
    getAnalyticsLists: builder.query<ListsResponse, AnalyticsRangeParams>({
      query: (args) => buildAnalyticsQuery('/analytics/lists', args),
      providesTags: ['Lists'],
    }),
    getAnalyticsCredits: builder.query<CreditsResponse, AnalyticsRangeParams>({
      query: (args) => buildAnalyticsQuery('/analytics/credits', args),
      providesTags: ['Credits'],
    }),
    getAnalyticsTeam: builder.query<TeamResponse, AnalyticsRangeParams>({
      query: (args) => buildAnalyticsQuery('/analytics/team', args),
      providesTags: ['Team'],
    }),
    getAnalyticsEnrichment: builder.query<
      EnrichmentTabResponse,
      AnalyticsRangeParams
    >({
      query: (args) => buildAnalyticsQuery('/analytics/enrichment', args),
      providesTags: ['Enrichment'],
    }),
    getAnalyticsMembers: builder.query<MembersResponse, void>({
      query: () => '/analytics/members',
      providesTags: ['Members'],
    }),
  }),
});

export const {
  useGetAnalyticsOverviewQuery,
  useGetAnalyticsListsQuery,
  useGetAnalyticsCreditsQuery,
  useGetAnalyticsTeamQuery,
  useGetAnalyticsEnrichmentQuery,
  useGetAnalyticsMembersQuery,
} = analyticsApi;
